import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { getRootUrl } from '@share-a-cart/shared-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getDeals } from '../api';
import {
  AMAZON_ITEM_RECHECK_MS,
  AmazonItemDealCard,
  DealCard,
  DealsFilters,
  DealsNewsletterCard,
  DealsSeoLinks,
  DealsViewToggle,
  amazonItemDealsQuery,
  assignDealSlugs,
  dealListFailed,
  dealPermalink,
  dealRowKey,
  dealRowSearchFields,
  dealsPath,
  dealsPathStoreSlug,
  indexDealSearch,
  isAmazonStore,
  matchesDealRow,
  mergeDealRows,
  parseCodeParam,
  parseDealCollection,
  parseDiscountParam,
  parseExpiringParam,
  popularStores,
  showableAmazonItems,
  storeSlug,
  uniqueStores,
  withPinnedStores,
  type DealCollectionSlug,
  type DealsView,
  type DiscountBucket,
} from '../components/deals';
import { SimpleSpinner } from '../components/common/SimpleSpinner';
import { useCurrentUser, useDebounce, useIsSchoolUser, useNow, useScrollToTop } from 'src/hooks';
import { localUrl } from 'src/utils';

const PAGE_SIZE = 20;
const CART_PAGE_SOURCES = ['get', 'receive', 'make', 'share', 'embed', 'widget'];

function collectionCopy(
  slug: DealCollectionSlug,
  t: (key: string, opts?: Record<string, string | number>) => string,
) {
  if (slug === 'latest') {
    return {
      title: t('deals.latest_page_title'),
      meta: t('deals.latest_meta_description'),
      heading: t('deals.latest_heading'),
    };
  }
  if (slug === 'expiring-soon') {
    return {
      title: t('deals.expiring_soon_page_title'),
      meta: t('deals.expiring_soon_meta_description'),
      heading: t('deals.expiring_soon_heading'),
    };
  }
  if (slug === 'promo-codes') {
    return {
      title: t('deals.promo_codes_page_title'),
      meta: t('deals.promo_codes_meta_description'),
      heading: t('deals.promo_codes_heading'),
    };
  }
  const pct = Number(slug.slice(0, 2));
  return {
    title: t('deals.discount_page_title', { pct }),
    meta: t('deals.discount_meta_description', { pct }),
    heading: t('deals.discount_heading', { pct }),
  };
}

export const Deals = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { store: storeParam } = useParams<{ store?: string }>();
  const navigate = useNavigate();
  const vendorQuery = searchParams.get('vendor');
  const collection = parseDealCollection(storeParam);
  const vendor = collection ? null : storeParam?.trim() ? storeParam.trim() : null;
  const discount = collection?.discount ?? parseDiscountParam(searchParams.get('discount'));
  const hasCode = collection?.hasCode || parseCodeParam(searchParams.get('code'));
  const expiringSoon = collection?.expiringSoon || parseExpiringParam(searchParams.get('expiring'));
  const latest = Boolean(collection?.latest);
  const fromCart = CART_PAGE_SOURCES.includes(searchParams.get('from') ?? '');
  const [search, setSearch] = useState('');
  const [debounceDelay, setDebounceDelay] = useState(500);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isSchoolUser = useIsSchoolUser();
  const { isLoading: userLoading } = useCurrentUser();

  // School users default to the schools view; an explicit ?view= param wins so
  // a shared link shows the same view to everyone.
  const viewParam = searchParams.get('view');
  const view: DealsView =
    viewParam === 'schools'
      ? 'schools'
      : viewParam === 'all'
        ? 'all'
        : isSchoolUser
          ? 'schools'
          : 'all';
  const showToggle = isSchoolUser || searchParams.has('view');

  useScrollToTop();

  // The landing view depends on the user's email (school default), so wait for
  // the currentUser query to settle before firing — otherwise school users
  // would be logged as landing on 'all'. Ref-guarded to fire exactly once.
  const firedPageView = useRef(false);
  useEffect(() => {
    if (userLoading || firedPageView.current) return;
    firedPageView.current = true;
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: 'Page View',
        eventValue: {
          source: searchParams.get('from') ?? 'direct',
          view,
          ...(vendor && { vendor }),
          ...(discount != null && { discount }),
          ...(hasCode && { code: true }),
          ...(expiringSoon && { expiring: true }),
          ...(latest && { latest: true }),
          ...(collection && { collection: collection.slug }),
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading]);

  const debouncedSearch = useDebounce(search, debounceDelay);

  const dealsQuery = useQuery({
    queryKey: ['deals'],
    queryFn: getDeals,
    staleTime: 1000 * 60 * 10,
  });

  const amazonItemsPage =
    view !== 'schools' && !hasCode && (vendor == null || isAmazonStore(vendor));
  const showAmazonItems = amazonItemsPage && !userLoading;
  const amazonItemsQuery = useQuery({ ...amazonItemDealsQuery, enabled: showAmazonItems });
  const awaitingAmazonItems = amazonItemsPage && (userLoading || amazonItemsQuery.isLoading);

  const dealRows = useMemo(() => {
    const allDeals = dealsQuery.data ?? [];
    const slugs = assignDealSlugs(allDeals);
    return allDeals.map((deal, i) => ({
      deal,
      path: dealPermalink(storeSlug(deal), slugs[i]),
      feedIndex: i,
    }));
  }, [dealsQuery.data]);
  const baseRows = useMemo(
    () => (view === 'schools' ? dealRows.filter((row) => row.deal.school) : dealRows),
    [dealRows, view],
  );
  const now = useNow(AMAZON_ITEM_RECHECK_MS);
  const amazonItemRows = useMemo(
    () =>
      showAmazonItems && !amazonItemsQuery.isError && amazonItemsQuery.data
        ? showableAmazonItems(amazonItemsQuery.data, now)
        : undefined,
    [showAmazonItems, amazonItemsQuery.isError, amazonItemsQuery.data, now],
  );
  const listRows = useMemo(
    () => mergeDealRows(baseRows, amazonItemRows ?? []),
    [baseRows, amazonItemRows],
  );
  const hasSearch = debouncedSearch.trim() !== '';
  const searchListRows = useMemo(
    () => (hasSearch ? indexDealSearch(listRows.map(dealRowSearchFields)) : null),
    [hasSearch, listRows],
  );
  const stores = useMemo(
    () => withPinnedStores(uniqueStores(dealsQuery.data ?? [])),
    [dealsQuery.data],
  );
  const seoStores = useMemo(() => popularStores(dealsQuery.data ?? []), [dealsQuery.data]);
  const storeLabel = stores.find((store) => store.slug === vendor)?.label ?? null;

  // Legacy `?vendor=` (display name or key) and mixed-case paths → `/deals/:store`.
  useEffect(() => {
    const allDeals = dealsQuery.data;
    if (!allDeals) return;
    if (vendorQuery?.trim()) {
      const slug = dealsPathStoreSlug(allDeals, vendorQuery);
      navigate(dealsPath(slug, searchParams), { replace: true });
      return;
    }
    if (storeParam && storeParam !== storeParam.toLowerCase()) {
      const slug = collection?.slug ?? dealsPathStoreSlug(allDeals, storeParam);
      if (slug && slug !== storeParam) {
        navigate(dealsPath(slug, searchParams), { replace: true });
      }
    }
  }, [dealsQuery.data, vendorQuery, storeParam, searchParams, navigate, collection?.slug]);

  const searchMatches = useMemo(
    () => (searchListRows ? searchListRows(debouncedSearch) : null),
    [searchListRows, debouncedSearch],
  );
  const filteredRows = listRows.filter(
    (row, i) =>
      matchesDealRow(row, { vendor, discount, hasCode, expiringSoon, latest }) &&
      (!searchMatches || searchMatches[i]),
  );
  const listFailed = dealListFailed(
    filteredRows.length,
    dealsQuery,
    showAmazonItems ? amazonItemsQuery : undefined,
  );

  const visibleRows = filteredRows.slice(0, visibleCount);
  const hasMore = visibleCount < filteredRows.length;
  const hasActiveFilters = Boolean(
    collection ||
    vendor ||
    discount != null ||
    hasCode ||
    expiringSoon ||
    latest ||
    debouncedSearch.trim(),
  );

  // Reset pagination when filter changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [debouncedSearch, view, vendor, discount, hasCode, expiringSoon, latest, collection?.slug]);

  const lastSearchQuery = useRef('');
  useEffect(() => {
    const query = debouncedSearch.trim();
    if (!query) {
      lastSearchQuery.current = '';
      return;
    }
    if (dealsQuery.status !== 'success' || awaitingAmazonItems) return;
    if (lastSearchQuery.current === query) return;
    lastSearchQuery.current = query;
    const count = filteredRows.length;
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: count === 0 ? 'Search No Results' : 'Search',
        eventValue: {
          query,
          ...(count > 0 && { resultCount: count }),
        },
      },
    });
  }, [debouncedSearch, dealsQuery.status, awaitingAmazonItems, filteredRows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((c) => c + PAGE_SIZE);
      },
      { rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredRows]);

  // Fire immediately when input is cleared
  useEffect(() => {
    if (!search) {
      setDebounceDelay(0);
      setTimeout(() => setDebounceDelay(500), 20);
    }
  }, [search]);

  // Enter fires immediately, then restores the debounce delay
  const handleEnterPress = () => {
    setDebounceDelay(0);
    setTimeout(() => setDebounceDelay(500), 20);
  };

  const updateFilterParams = (patch: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === '') params.delete(key);
      else params.set(key, value);
    }
    setSearchParams(params, { replace: true });
  };

  const trackFilter = (eventValue: Record<string, string | number | boolean>) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: 'Filter',
        eventValue,
      },
    });
  };

  // Always write the param explicitly so a non-school visitor arriving on a
  // shared ?view=schools link can toggle both directions (the toggle stays
  // visible as long as the param is present).
  const handleViewChange = (next: DealsView) => {
    if (next === view) return;
    updateFilterParams({ view: next });
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: 'View Toggle',
        eventValue: { view: next },
      },
    });
  };

  const handleVendorChange = (next: string | null) => {
    if (next === vendor || (next == null && vendor == null)) return;
    navigate(dealsPath(next, searchParams), { replace: true });
    trackFilter({ filter: 'vendor', vendor: next ?? 'all' });
  };

  const handleDiscountChange = (next: DiscountBucket | null) => {
    if (next === discount) return;
    updateFilterParams({ discount: next == null ? null : String(next) });
    trackFilter({ filter: 'discount', discount: next ?? 'all' });
  };

  const handleHasCodeChange = (next: boolean) => {
    if (next === hasCode) return;
    updateFilterParams({ code: next ? '1' : null });
    trackFilter({ filter: 'code', code: next });
  };

  const handleExpiringSoonChange = (next: boolean) => {
    if (next === expiringSoon) return;
    updateFilterParams({ expiring: next ? '1' : null });
    trackFilter({ filter: 'expiring', expiring: next });
  };

  const handleClearFilters = () => {
    if (!hasActiveFilters) return;
    setSearch('');
    const params = new URLSearchParams();
    const from = searchParams.get('from');
    const viewValue = searchParams.get('view');
    if (from) params.set('from', from);
    if (viewValue) params.set('view', viewValue);
    navigate(dealsPath(null, params), { replace: true });
    trackFilter({ filter: 'clear' });
  };

  const emptyMessage = debouncedSearch.trim()
    ? `No deals found for "${debouncedSearch}".`
    : hasActiveFilters
      ? 'No deals match your filters.'
      : view === 'schools'
        ? 'No school deals available right now. Check back soon!'
        : 'No deals available right now. Check back soon!';

  const collectionMeta = collection ? collectionCopy(collection.slug, t) : null;
  const pageTitle = collectionMeta
    ? collectionMeta.title
    : storeLabel
      ? t('deals.store_page_title', { store: storeLabel })
      : t('deals.page_title');
  const metaDescription = collectionMeta
    ? collectionMeta.meta
    : storeLabel
      ? isAmazonStore(vendor)
        ? t('deals.amazon.meta_description')
        : t('deals.store_meta_description', { store: storeLabel })
      : t('deals.meta_description');
  const canonicalPath = collection
    ? `/deals/${collection.slug}`
    : vendor
      ? `/deals/${vendor}`
      : '/deals';
  const canonical = `${getRootUrl()}${localUrl(canonicalPath)}`;
  const heading = collectionMeta
    ? collectionMeta.heading
    : storeLabel
      ? `${storeLabel} Deals`
      : view === 'schools'
        ? 'Deals for Schools'
        : 'Deals';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name='description' content={metaDescription} />
        <link rel='canonical' href={canonical} />
      </Helmet>

      {/* Hero */}
      <div className='relative flex w-full flex-col items-center gap-3 bg-primary-dark px-4 py-4'>
        {fromCart && (
          <button
            onClick={() => navigate(-1)}
            className='heading-100 inline-flex items-center self-start whitespace-nowrap text-skyBlue transition-all hover:text-white'
          >
            <ChevronLeftIcon className='-ml-1.5 -mr-0.5 -mt-0.5' />
            Back to Cart
          </button>
        )}
        <div className='flex flex-col items-center gap-1 text-center'>
          <h1 className='heading-500 text-offWhite'>{heading}</h1>
          <p className='text-200 text-blueGray'>
            {view === 'schools'
              ? 'Deals for schools, educators, and students. '
              : isAmazonStore(vendor)
                ? `${t('deals.amazon.hero')} `
                : 'Special deals, only for Share-A-Cart users. '}
            <a
              href={localUrl('/save')}
              data-attr='deals-readmore-save'
              className='text-skyBlue underline transition-opacity duration-150 hover:opacity-80'
            >
              Read more
            </a>
          </p>
        </div>

        {showToggle && (
          <div className='w-full max-w-6xl'>
            <DealsViewToggle view={view} onChange={handleViewChange} />
          </div>
        )}

        <div className='flex w-full max-w-6xl flex-col items-center gap-2 sm:gap-3'>
          <div className='flex w-full max-w-md flex-row flex-nowrap items-center justify-center rounded-md'>
            <div className='flex grow flex-row flex-nowrap'>
              <button
                type='button'
                onClick={handleEnterPress}
                className='hidden h-10 items-center justify-center rounded-l border-2 border-r-0 border-primary bg-primary text-white transition hover:cursor-pointer hover:bg-primary-hover xs:flex'
              >
                <i className='material-icons-outlined px-1.5 py-1.5 text-[20px] leading-none text-white'>
                  search
                </i>
              </button>
              <input
                className='h-10 w-full rounded-l border-2 border-r-0 border-cloudGray bg-white px-2 py-1.5 font-secondary text-sm font-normal text-primary-dark transition hover:bg-cloudGray focus:border-primary focus:outline-none focus:ring-0 xs:grow xs:rounded-none xs:border-x-0 xs:px-3'
                type='text'
                value={search}
                placeholder='Search by keyword'
                aria-label='Search by keyword'
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleEnterPress();
                }}
              />
            </div>
            <button
              type='button'
              onClick={handleEnterPress}
              className='h-10 rounded-r border-2 border-l-0 border-primary bg-primary px-3 py-1.5 font-secondary text-sm text-white transition hover:cursor-pointer hover:bg-primary-hover'
            >
              {t('common.search')}
            </button>
          </div>
          <DealsFilters
            stores={stores}
            vendor={vendor}
            discount={discount}
            hasCode={hasCode}
            expiringSoon={expiringSoon}
            canClear={hasActiveFilters}
            onVendorChange={handleVendorChange}
            onDiscountChange={handleDiscountChange}
            onHasCodeChange={handleHasCodeChange}
            onExpiringSoonChange={handleExpiringSoonChange}
            onClear={handleClearFilters}
          />
        </div>
      </div>

      {/* Deals list */}
      <div className='mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 pb-6 pt-3'>
        {debouncedSearch.trim() && (
          <span className='text-200 text-primary-dark'>
            Showing results for &ldquo;{debouncedSearch}&rdquo;
          </span>
        )}

        {!dealsQuery.isError && !awaitingAmazonItems && filteredRows.length > 0 && (
          <p className='text-100 text-offGray'>
            {t('deals.offers_available', { count: filteredRows.length })}
          </p>
        )}

        {dealsQuery.isLoading || (awaitingAmazonItems && filteredRows.length === 0) ? (
          <div className='mx-auto py-20'>
            <SimpleSpinner customWidth='w-16' customHeight='h-16' customPosition='block' />
          </div>
        ) : listFailed ? (
          <div className='py-20 text-center'>
            <p className='text-200 text-offGray'>
              Something went wrong loading deals. Please try again.
            </p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className='flex flex-col items-center gap-3 py-20 text-center'>
            <p className='text-200 text-offGray'>{emptyMessage}</p>
            {hasActiveFilters && (
              <button
                type='button'
                onClick={handleClearFilters}
                className='font-secondary text-base font-semibold text-primary transition-opacity hover:opacity-80'
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className='grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3'>
              {visibleRows.map((row) =>
                row.kind === 'curated' ? (
                  <DealCard key={dealRowKey(row)} deal={row.deal} path={row.path} />
                ) : (
                  <AmazonItemDealCard key={dealRowKey(row)} item={row.item} />
                ),
              )}
            </div>
            {hasMore && <div ref={sentinelRef} className='h-1' />}
          </>
        )}

        <DealsSeoLinks currentSlug={collection?.slug ?? vendor} stores={seoStores} />
      </div>

      <DealsNewsletterCard />
    </>
  );
};
